/**
 * Reaching for `sessionStorage` without letting it take the screen down with it.
 *
 * ## Why a shape check and not just `try/catch`
 *
 * A `try/catch` is genuinely required — a private window, a browser configured to block site data,
 * or a cross-origin iframe all make `window.sessionStorage` *throw on access*, and an unguarded
 * `window.sessionStorage.getItem(...)` therefore crashes the component that called it. That was the
 * live state of `lib/authorNavigation.ts` until 2026-08-28: five unguarded calls, one of them on the
 * course browser's first render, so a storage-blocked browser lost the Courses screen entirely.
 *
 * The **shape check** answers a different failure, measured in this repo on 2026-08-28 and recorded
 * as a lane finding: under `vitest` + `jsdom` as configured in `packages/web/vite.config.ts`,
 * `window.localStorage` is an empty null-prototype object — `typeof localStorage` is `'object'`, and
 * `localStorage.setItem` is `undefined`. A truthiness check passes and the very next line throws a
 * `TypeError` that the `catch` swallows, so a storage-backed feature degrades **silently** and every
 * test of it becomes a *cannot-pass* check dressed as a passing one. `sessionStorage` behaves
 * normally in the same environment, which is one of the two reasons the remembered review level uses
 * it (`lib/courseSelection.ts` carries the other: per-tab memory cannot let one tab's exam level
 * decide what another tab opens).
 *
 * **So: both guards, always.** Anything in this codebase that reaches for web storage goes through
 * here rather than re-deciding it.
 */
export function sessionStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const candidate = window.sessionStorage;
    return typeof candidate?.getItem === 'function'
      && typeof candidate.setItem === 'function'
      && typeof candidate.removeItem === 'function'
      ? candidate
      : null;
  } catch {
    return null;
  }
}

/**
 * ...AND THE METHODS THROW TOO, WHICH THE SHAPE CHECK ALONE DOES NOT COVER.
 *
 * {@link sessionStore} answers "is there a usable storage object?" — it guards the *property* access
 * and the missing-method shape. It does not guard the CALL: a `Storage`-shaped object whose
 * `getItem` exists and throws (a browser enforcing a site-data policy at call time, a quota failure
 * on write, a sandboxed iframe) still takes down whatever called it. `gpt-5.6-sol` named exactly
 * this gap on 2026-08-28: the property was guarded, `getItem()` was not, and the caller
 * (`lib/authorNavigation.ts`, on the Courses screen's first render) would still have crashed.
 *
 * These three are therefore the only supported way to touch session storage in this app. Each one
 * answers with the shape a caller can always use — a value or `null`, or nothing at all — so a
 * blocked browser costs the reviewer a remembered convenience and never a screen.
 */
export function readSessionValue(key: string): string | null {
  try {
    return sessionStore()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeSessionValue(key: string, value: string): void {
  try {
    sessionStore()?.setItem(key, value);
  } catch {
    /* a full quota is the other way this throws; losing the memory costs a click, not the screen */
  }
}

export function removeSessionValue(key: string): void {
  try {
    sessionStore()?.removeItem(key);
  } catch {
    /* see above */
  }
}

/** Local presentation preferences survive a tab close; access and method calls can both throw. */
function localStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const candidate = window.localStorage;
    return typeof candidate?.getItem === 'function'
      && typeof candidate.setItem === 'function'
      && typeof candidate.removeItem === 'function' ? candidate : null;
  } catch { return null; }
}

export function readLocalValue(key: string): string | null {
  try { return localStore()?.getItem(key) ?? null; } catch { return null; }
}
export function writeLocalValue(key: string, value: string): void {
  try { localStore()?.setItem(key, value); } catch { /* preference remains usable without persistence */ }
}
export function removeLocalValue(key: string): void {
  try { localStore()?.removeItem(key); } catch { /* reset remains usable without persistence */ }
}
